import { CSSProperties } from "react"; import { useEffect, useState } from 'react';
import useCampaignBuilder from "Hooks/useCampaignBuilder";
import Colors from "modules/Colors";
import { clsx, styx } from "modules/Utils";
import CardSymphony from 'elements/CardSymphony';
import useFacebookConnections from "Hooks/useFacebookConnections";
import { AssetPlatformType } from "./AdCreativeSelectorCampaignView";
import { transformPostsToCreativeFormat } from "Hooks/useCampaignBuilder";
import TabsSymphony, { TabItem } from "elements/TabsSymphony";
import { CreativeSelectorPost } from "Hooks/useCampaignBuilder";
import InputSymphony, { SelectOptionItem } from "elements/InputSymphony";
import useScreen from "Hooks/useScreen";
import AdPreview from "elements/AdPreview";

export const getPreviewTabOptions = (variant: 'tiktok' | 'facebook'): TabItem[] => {
    if (variant === 'facebook') {
        return [
            {
                id: 'ig-story',
                title: 'Story'
            },
            {
                id: 'ig-reels',
                title: 'Reels'
            },

            {
                id: 'ig-feed',
                title: 'Feed'
            }
        ]
    }

    return [
        {
            id: 'tiktok',
            title: 'TikTok'
        },
    ]
}

interface AdPreviewCampaignViewProps {
    className?: string;
    style?: CSSProperties;
    variant: 'tiktok' | 'facebook';
    hidePlatformSelector?: boolean;
    hideTitle?: boolean;
    hideSubtitle?: boolean;
    overridePreviewType?: 'ig-story' | 'ig-reels' | 'ig-feed';
}

const AdPreviewCampaignView = ({
    className,
    style,
    variant = 'facebook',
    hidePlatformSelector = false,
    hideTitle = false,
    hideSubtitle = false,
    overridePreviewType,
}: AdPreviewCampaignViewProps) => {
    const { mobileView, mStyle } = useScreen();
    const {
        selectedInstagramPosts,
        uploadedAssets,
        callToAction,
        selectedSparkCodes,
        previewedAsset,
        setPreviewedAsset,
        captions,
        getAllSelectedPosts
    } = useCampaignBuilder();

    const {
        selectedInstagramPage,
        selectedFacebookPage,
    } = useFacebookConnections()

    const PREVIEW_TAB_OPTIONS = getPreviewTabOptions(variant)
    const [previewTypeTab, setPreviewTypeTab] = useState<TabItem | null>(() => {
        if (overridePreviewType) {
            return PREVIEW_TAB_OPTIONS.find(tab => tab.id === overridePreviewType) || null
        }
        return null
    })

    let transformedPosts: CreativeSelectorPost[] = getAllSelectedPosts(variant)

    useEffect(() => {
        if (transformedPosts.length > 0 && !previewTypeTab) {
            setPreviewTypeTab(PREVIEW_TAB_OPTIONS[0])
        } else if (!transformedPosts.length && previewTypeTab) {
            setPreviewTypeTab(null)
        }
    }, [transformedPosts])

    // Auto-select first asset if none is selected
    useEffect(() => {
        if (transformedPosts.length > 0 && !previewedAsset) {
            setPreviewedAsset(transformedPosts[0])
        }
    }, [transformedPosts, previewedAsset])

    useEffect(() => {
        if (overridePreviewType) {
            setPreviewTypeTab(PREVIEW_TAB_OPTIONS.find(tab => tab.id === overridePreviewType) || null)
        }
    }, [overridePreviewType])


    const handleTabChange = (tab: string | TabItem) => {
        const tabItem = tab as TabItem
        setPreviewTypeTab(tabItem)
    }

    const getPlatformProfileDetails = (): { username: string | null, profilePic: string | null } => {
        let username: string | null = null
        let profilePic: string | null = null

        if (variant === 'facebook') {
            if (selectedInstagramPage) {
                username = selectedInstagramPage.instagramAccount.username
                profilePic = selectedInstagramPage.instagramAccount.profile_picture_url
            } else if (selectedFacebookPage) {
                username = selectedFacebookPage.name
                profilePic = selectedFacebookPage.picture && selectedFacebookPage.picture.data ? selectedFacebookPage.picture.data.url : null
            }
        } else if (variant === 'tiktok' && selectedSparkCodes.length > 0) {
            // Get TikTok profile details from the first selected spark code
            const sparkCode = previewedAsset ? selectedSparkCodes.find(code => code.item_info?.item_id === previewedAsset.id) : null    
            console.log('sparkCode', sparkCode)
            username = sparkCode?.user_info?.tiktok_name || null
            profilePic = sparkCode?.user_info?.profile_image || null
        }
        return { username, profilePic }
    }

    const {
        username,
        profilePic
    } = getPlatformProfileDetails()


    const renderAdPreviewTabs = () => {
        if (!previewTypeTab || !previewedAsset || hidePlatformSelector) return null
        return (
            <>
                {transformedPosts.length > 1 &&
                    <InputSymphony
                        className="adPreviewCampaignAssetSelector"
                        isSelectDropdown
                        value={previewedAsset?.id || ''}
                        options={transformedPosts.map((post, index) => {
                            let platform: string = ''
                            switch (post.originalPostType) {
                                case AssetPlatformType.INSTAGRAM:
                                    platform = 'Instagram Post'
                                    break
                                case AssetPlatformType.TIKTOK:
                                    platform = 'TikTok'
                                    break
                                case AssetPlatformType.CLOUDINARY:
                                    platform = 'Uploaded'
                                    break
                                case AssetPlatformType.IMPORTED_FROM_URL:
                                    platform = 'Imported from URL'
                                    break
                            }
                            return {
                                title: `Ad ${index + 1} - ${platform}`,
                                value: post.id,
                                description: ``
                            } as SelectOptionItem
                        })}
                        onOptionSelect={(option: SelectOptionItem) => {
                            const selectedPost = transformedPosts.find(post => post.id === option.value)
                            if (selectedPost) {
                                setPreviewedAsset(selectedPost)
                            }
                        }}
                    />
                }
                <TabsSymphony
                    className="adPreviewTabs"
                    selectedTab={previewTypeTab}
                    tabs={PREVIEW_TAB_OPTIONS}
                    onChange={handleTabChange}
                    aria-label="Settings Options Selector"
                />
            </>
        )
    }

    // Determine if the current preview type is vertical format (Stories/Reels) or TikTok
    const isVerticalFormat = previewTypeTab?.id === 'ig-story' || previewTypeTab?.id === 'ig-reels';
    const isFeedFormat = previewTypeTab?.id === 'ig-feed';
    const isTikTokFormat = previewTypeTab?.id === 'tiktok';

    // Get the subtitle text based on preview type
    const getSubtitleText = () => {
        if (variant === 'tiktok') {
            return 'This is what your ad will look like when it\'s run on TikTok.';
        }
        
        if (previewTypeTab?.id === 'ig-story') {
            return 'This is what your ad will look like when it\'s run on Instagram Stories.';
        } else if (previewTypeTab?.id === 'ig-reels') {
            return 'This is what your ad will look like when it\'s run on Instagram Reels.';
        } else {
            return 'This is what your ad will look like when it\'s run on Instagram Feed.';
        }
    };

    return (
        <div
            className={clsx('adPreviewCampaignViewComponent', className)}
            style={styx(sty.adPreviewCampaignViewComponent, style)}
        >
            <div
                className="adPreviewHeaderContainer"
                style={mStyle(sty, 'adPreviewHeaderContainer')}
            >
                {!hideTitle && (
                    <CardSymphony
                        className="adPreviewHeader"
                        title="Ad Preview"
                        subtitle={hideSubtitle ? undefined : getSubtitleText()}
                        isNoPadding
                        size="small"
                    />
                )}
                {renderAdPreviewTabs()}
            </div>
            <div 
                className={clsx('adPreviewWrapper', {
                    'verticalPreviewWrapper': isVerticalFormat,
                    'feedPreviewWrapper': isFeedFormat
                })}
                style={sty.adPreviewWrapper}
            >
                <div 
                    className={clsx('adPreviewContainer', {
                        'verticalFormatContainer': isVerticalFormat,
                        'feedFormatContainer': isFeedFormat,
                        'tiktokFormatContainer': isTikTokFormat
                    })}
                    style={
                        isVerticalFormat 
                            ? sty.verticalFormatContainer 
                            : isFeedFormat 
                                ? sty.feedFormatContainer 
                                : isTikTokFormat
                                    ? sty.tiktokFormatContainer
                                    : sty.regularFormatContainer
                    }
                >
                    <AdPreview
                        className={clsx({
                            'verticalAdPreview': isVerticalFormat,
                            'feedAdPreview': isFeedFormat,
                            'tiktokAdPreview': isTikTokFormat
                        })}
                        username={username}
                        callToActionLabel={callToAction.label}
                        profilePicUrl={profilePic}
                        previewedAsset={previewedAsset}
                        captions={captions}
                        previewType={previewTypeTab?.id ?? null}
                        style={
                            isVerticalFormat 
                                ? sty.verticalAdPreview 
                                : isFeedFormat 
                                    ? sty.feedAdPreview 
                                    : isTikTokFormat
                                        ? sty.tiktokAdPreview
                                        : sty.regularAdPreview
                        }
                    />
                </div>
            </div>
        </div>
    )
}
const sty: Record<string, CSSProperties> = {
    adPreviewCampaignViewComponent: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white'
    },
    adPreviewHeaderContainer: {
        paddingTop: 16,
        paddingLeft: 16,
        paddingRight: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 8
    },
    adPreviewHeaderContainer_mobile: {
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
    },
    videoAsImageWarning: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 8,
        gap: 10,
        background: Colors.PAPER_LIGHT_CONTAINER,
        width: '100%',
        borderTop: '1px solid rgba(229, 231, 235)'
    },
    storyParentDiv_mobile: {
        width: '250px'
    },
    callToActionSimple: {
        fontSize: '0.5vw',
        width: '12.5vw',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        aspectRatio: '3',
        borderRadius: 9999,
        backgroundColor: 'white',
        opacity: 0.5,
        color: 'white',
        fontWeight: 600
    },
    callToActionSimple_mobile: {
        fontSize: '1.5vw',
        width: '12.5vw',
    },
    // Outer wrapper for preview content
    adPreviewWrapper: {
        width: '100%',
        padding: 16,
        display: 'flex',
        justifyContent: 'center'
    },
    // Container for vertical formats (Stories and Reels) to enforce 9:16 aspect ratio
    verticalFormatContainer: {
        width: '100%',
        maxWidth: 360,
        margin: '0 auto',
        aspectRatio: '9/16',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid rgba(229, 231, 235)',
        borderRadius: 8,
        backgroundColor: 'black'
    },
    // Container for feed format
    feedFormatContainer: {
        width: '100%',
        maxWidth: 450,
        display: 'flex',
        justifyContent: 'center',
        borderRadius: 16,
        overflow: 'hidden',
        border: '1px solid rgba(229, 231, 235)',
    },
    // Container for regular formats
    regularFormatContainer: {
        width: '100%',
        maxWidth: 450,
        display: 'flex',
        justifyContent: 'center',
        borderRadius: 8,
        overflow: 'hidden'
    },
    // Style for vertical ad previews (Stories and Reels)
    verticalAdPreview: {
        width: '100%',
        height: '100%',
        aspectRatio: '9/16',
        overflow: 'hidden',
        padding: 0,
        borderRadius: 0,
        boxShadow: 'none'
    },
    // Style for feed ad previews
    feedAdPreview: {
        width: '100%',
        overflow: 'hidden',
        padding: 0,
        borderRadius: 0,
        boxShadow: 'none'
    },
    // Style for regular feed previews
    regularAdPreview: {
        width: '100%',
        borderRadius: 8,
        overflow: 'hidden'
    },
    // Container for TikTok format
    tiktokFormatContainer: {
        width: '100%',
        maxWidth: 360,
        margin: '0 auto',
        aspectRatio: '9/16',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: '1px solid rgba(229, 231, 235)',
        borderRadius: 8,
        backgroundColor: 'black'
    },
    // Style for TikTok ad previews
    tiktokAdPreview: {
        width: '100%',
        height: '100%',
        aspectRatio: '9/16',
        overflow: 'hidden',
        padding: 0,
        borderRadius: 0,
        boxShadow: 'none'
    }
}

export default AdPreviewCampaignView;